import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Link,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle,
  Error as ErrorIcon,
  HelpOutline,
  Storage as StorageIcon,
  SmartToy as AgentIcon,
  Api as ApiIcon,
  Cloud as CloudIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/GoogleAuthContext';
import { api, Service, ServicesResponse } from '../services/api-client';
import { formatDistanceToNow } from 'date-fns';

// =============================================================================
// Types
// =============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type ServiceStatus = 'all' | 'active' | 'inactive';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// =============================================================================
// Components
// =============================================================================

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function ServiceCard({
  service,
  onEdit,
}: {
  service: Service;
  onEdit: () => void;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'developer';

  const getHealthIcon = () => {
    if (!service.active) {
      return <ErrorIcon sx={{ color: 'text.disabled' }} />;
    }
    const status = service.runtime?.health_status;
    switch (status) {
      case 'healthy':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'unhealthy':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'unreachable':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      default:
        return <HelpOutline sx={{ color: 'text.secondary' }} />;
    }
  };

  const getTypeIcon = () => {
    switch (service.type) {
      case 'mcp_server':
        return <StorageIcon />;
      case 'agent':
        return <AgentIcon />;
      case 'api':
        return <ApiIcon />;
      default:
        return <CloudIcon />;
    }
  };

  const getTypeLabel = () => {
    switch (service.type) {
      case 'mcp_server':
        return 'MCP Server';
      case 'agent':
        return 'AI Agent';
      case 'api':
        return 'API';
      default:
        return 'Service';
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        opacity: service.active ? 1 : 0.6,
        border: service.active ? undefined : '1px dashed',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
            {getHealthIcon()}
            <Tooltip title={service.id}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {service.id}
              </Typography>
            </Tooltip>
          </Box>
          {canEdit && (
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Type & Status */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={getTypeIcon()}
            label={getTypeLabel()}
            size="small"
            variant="outlined"
          />
          {!service.active && (
            <Chip label="Inactive" size="small" color="default" />
          )}
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            minHeight: 40,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {service.description || 'No description provided'}
        </Typography>

        {/* Deployment Info */}
        {service.deployment && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight="medium">
              Deployment
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
              {service.deployment.image_tag && (
                <Tooltip title="View commit on GitHub">
                  <Link
                    href={`https://github.com/byai-group/chatbot-ai/commit/${service.deployment.image_tag}`}
                    target="_blank"
                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                  >
                    {service.deployment.image_tag.substring(0, 7)}
                  </Link>
                </Tooltip>
              )}
              {service.deployment.deployed_at && (
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(service.deployment.deployed_at), { addSuffix: true })}
                </Typography>
              )}
            </Box>
            {service.deployment.deployed_by && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                by {service.deployment.deployed_by.split('@')[0]}
              </Typography>
            )}
          </Box>
        )}

        {/* Runtime Info */}
        {service.runtime?.version && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              v{service.runtime.version}
              {service.runtime.transport && ` • ${service.runtime.transport}`}
            </Typography>
          </Box>
        )}

        {/* Used By */}
        {service.used_by && service.used_by.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight="medium">
              Used by {service.used_by.length} agent{service.used_by.length !== 1 ? 's' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {service.used_by.slice(0, 3).map(agent => (
                <Chip
                  key={agent}
                  label={agent}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {service.used_by.length > 3 && (
                <Chip
                  label={`+${service.used_by.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* URL */}
        {service.url && (
          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Tooltip title={service.url}>
              <Link
                href={service.url}
                target="_blank"
                sx={{
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
                {(() => {
                  try {
                    return new URL(service.url).hostname;
                  } catch {
                    return service.url;
                  }
                })()}
              </Link>
            </Tooltip>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({
  type,
  onSync
}: {
  type: string;
  onSync: () => void;
}) {
  const getIcon = () => {
    switch (type) {
      case 'mcp_server':
        return <StorageIcon sx={{ fontSize: 64, color: 'text.disabled' }} />;
      case 'agent':
        return <AgentIcon sx={{ fontSize: 64, color: 'text.disabled' }} />;
      case 'api':
        return <ApiIcon sx={{ fontSize: 64, color: 'text.disabled' }} />;
      default:
        return <CloudIcon sx={{ fontSize: 64, color: 'text.disabled' }} />;
    }
  };

  const getLabel = () => {
    switch (type) {
      case 'mcp_server':
        return 'MCP Servers';
      case 'agent':
        return 'AI Agents';
      case 'api':
        return 'APIs';
      default:
        return 'Services';
    }
  };

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        px: 4,
        bgcolor: 'action.hover',
        borderRadius: 2,
        border: '2px dashed',
        borderColor: 'divider',
      }}
    >
      {getIcon()}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        No {getLabel()} Found
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
        {getLabel()} will appear here after deployment.
        Try syncing from Cloud Run to discover deployed services.
      </Typography>
      <Button
        variant="contained"
        startIcon={<RefreshIcon />}
        onClick={onSync}
      >
        Sync from Cloud Run
      </Button>
    </Box>
  );
}

function ConfirmDisableDialog({
  service,
  open,
  onClose,
  onConfirm,
  loading,
}: {
  service: Service | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  if (!service) return null;

  const hasUsers = service.used_by && service.used_by.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        {service.active ? 'Disable' : 'Enable'} {service.id}?
      </DialogTitle>
      <DialogContent>
        {service.active && hasUsers && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              This service is currently used by {service.used_by?.length} agent{service.used_by?.length !== 1 ? 's' : ''}:
            </Typography>
            <Box sx={{ mt: 1 }}>
              {service.used_by?.map(agent => (
                <Chip key={agent} label={agent} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
            </Box>
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          {service.active
            ? 'Disabling this service will prevent agents from accessing it. The service will remain deployed but won\'t be used.'
            : 'Enabling this service will allow agents with permission to access it.'
          }
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color={service.active ? 'warning' : 'primary'}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : (service.active ? 'Disable Service' : 'Enable Service')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditServiceDialog({
  service,
  open,
  onClose,
  onSave,
  onToggleActive,
  loading,
}: {
  service: Service | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<Service>) => void;
  onToggleActive: () => void;
  loading: boolean;
}) {
  const [formData, setFormData] = useState<Partial<Service>>({});
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // Reset form when service changes
  useState(() => {
    if (service) {
      setFormData({
        name: service.name || service.id,
        description: service.description || '',
        type: service.type,
        active: service.active,
        url: service.url,
      });
    }
  });

  // Update form when service changes
  if (service && formData.name === undefined) {
    setFormData({
      name: service.name || service.id,
      description: service.description || '',
      type: service.type,
      active: service.active,
      url: service.url,
    });
  }

  if (!service) return null;

  const handleActiveToggle = () => {
    if (service.active && service.used_by && service.used_by.length > 0) {
      setShowDisableConfirm(true);
    } else {
      onToggleActive();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Service: {service.id}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Display Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Describe what this service does..."
            />
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type || 'service'}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as Service['type'] })}
                label="Type"
              >
                <MenuItem value="mcp_server">MCP Server</MenuItem>
                <MenuItem value="agent">AI Agent</MenuItem>
                <MenuItem value="api">API</MenuItem>
                <MenuItem value="service">Service</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="URL"
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              fullWidth
              size="small"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={service.active}
                    onChange={handleActiveToggle}
                    color={service.active ? 'success' : 'default'}
                  />
                }
                label={service.active ? 'Active' : 'Inactive'}
              />
              {service.used_by && service.used_by.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Used by {service.used_by.length} agent{service.used_by.length !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => onSave(formData)}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDisableDialog
        service={service}
        open={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onConfirm={() => {
          setShowDisableConfirm(false);
          onToggleActive();
        }}
        loading={loading}
      />
    </>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function Services() {
  const queryClient = useQueryClient();

  // State
  const [tab, setTab] = useState(0);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ServiceStatus>('all');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch services with used_by info
  const { data, isLoading, error } = useQuery<ServicesResponse>(
    'services',
    () => api.getServices({ includeUsedBy: true }),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Sync mutation
  const syncMutation = useMutation(
    () => api.syncServicesMetadata(),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries('services');
        setSnackbar({
          open: true,
          message: `Synced ${result.synced_count} services from Cloud Run`,
          severity: 'success',
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: 'Failed to sync from Cloud Run',
          severity: 'error',
        });
      },
    }
  );

  // Update service mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Service> }) => api.updateService(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('services');
        setEditingService(null);
        setSnackbar({
          open: true,
          message: 'Service updated successfully',
          severity: 'success',
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: 'Failed to update service',
          severity: 'error',
        });
      },
    }
  );

  // Handlers
  const handleSync = () => {
    syncMutation.mutate();
  };

  const handleSaveService = (formData: Partial<Service>) => {
    if (editingService) {
      updateMutation.mutate({ id: editingService.id, data: formData });
    }
  };

  const handleToggleActive = () => {
    if (editingService) {
      updateMutation.mutate({
        id: editingService.id,
        data: { active: !editingService.active }
      });
    }
  };

  // Filter services
  const services = data?.services || [];

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          service.id.toLowerCase().includes(query) ||
          service.name?.toLowerCase().includes(query) ||
          service.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter === 'active' && !service.active) return false;
      if (statusFilter === 'inactive' && service.active) return false;

      return true;
    });
  }, [services, searchQuery, statusFilter]);

  const mcpServers = filteredServices.filter(s => s.type === 'mcp_server');
  const agents = filteredServices.filter(s => s.type === 'agent');
  const apis = filteredServices.filter(s => s.type === 'api' || s.type === 'service' || !s.type);

  const getTabServices = () => {
    switch (tab) {
      case 0: return mcpServers;
      case 1: return agents;
      case 2: return apis;
      default: return filteredServices;
    }
  };

  const getTabType = (): string => {
    switch (tab) {
      case 0: return 'mcp_server';
      case 1: return 'agent';
      case 2: return 'api';
      default: return 'all';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Services</Typography>
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading services...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Services</Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => queryClient.invalidateQueries('services')}>
              Retry
            </Button>
          }
        >
          Failed to load services. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4">Services</Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.last_synced ? (
              <>Last synced {formatDistanceToNow(new Date(data.last_synced), { addSuffix: true })}</>
            ) : (
              'Never synced'
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={syncMutation.isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleSync}
          disabled={syncMutation.isLoading}
        >
          {syncMutation.isLoading ? 'Syncing...' : 'Sync Metadata'}
        </Button>
      </Box>

      {/* Sync progress */}
      {syncMutation.isLoading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Fetching metadata from Cloud Run...
          </Typography>
        </Box>
      )}

      {/* Search & Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search services..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ServiceStatus)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<StorageIcon />}
          label={`${mcpServers.length} MCP Server${mcpServers.length !== 1 ? 's' : ''}`}
          color={tab === 0 ? 'primary' : 'default'}
          variant={tab === 0 ? 'filled' : 'outlined'}
          onClick={() => setTab(0)}
        />
        <Chip
          icon={<AgentIcon />}
          label={`${agents.length} Agent${agents.length !== 1 ? 's' : ''}`}
          color={tab === 1 ? 'secondary' : 'default'}
          variant={tab === 1 ? 'filled' : 'outlined'}
          onClick={() => setTab(1)}
        />
        <Chip
          icon={<ApiIcon />}
          label={`${apis.length} API${apis.length !== 1 ? 's' : ''}`}
          color={tab === 2 ? 'info' : 'default'}
          variant={tab === 2 ? 'filled' : 'outlined'}
          onClick={() => setTab(2)}
        />
        <Chip
          icon={<CloudIcon />}
          label={`${filteredServices.length} Total`}
          color={tab === 3 ? 'success' : 'default'}
          variant={tab === 3 ? 'filled' : 'outlined'}
          onClick={() => setTab(3)}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`MCP Servers (${mcpServers.length})`} />
        <Tab label={`AI Agents (${agents.length})`} />
        <Tab label={`APIs & Services (${apis.length})`} />
        <Tab label={`All (${filteredServices.length})`} />
      </Tabs>

      {/* Content */}
      {[0, 1, 2, 3].map(tabIndex => (
        <TabPanel key={tabIndex} value={tab} index={tabIndex}>
          {getTabServices().length === 0 ? (
            <EmptyState type={getTabType()} onSync={handleSync} />
          ) : (
            <Grid container spacing={3}>
              {getTabServices().map(service => (
                <Grid item xs={12} md={6} lg={4} key={service.id}>
                  <ServiceCard
                    service={service}
                    onEdit={() => setEditingService(service)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>
      ))}

      {/* Edit Dialog */}
      <EditServiceDialog
        service={editingService}
        open={!!editingService}
        onClose={() => setEditingService(null)}
        onSave={handleSaveService}
        onToggleActive={handleToggleActive}
        loading={updateMutation.isLoading}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
